import { useEffect } from 'react';
import axios from 'axios';

import { versionOptions } from '../mappers';
import {
  apiRequestSuccess,
  apiRequestFailure,
  selectedVersion,
} from '../actions';

// const DOMAIN = `http://localhost:3003/${process.env.AWS_STACK_NAME}`; // Local serverless env
const DOMAIN = `https://fleet-cars-api.hmhost.co.uk/${process.env.AWS_STACK_NAME}`;

// Fetch the make options
const fetchMakeOptions = async (dispatch, source) => {
  try {
    const {
      data: { results },
    } = await axios({
      url: `${DOMAIN}/cars`,
      cancelToken: source.token,
    });

    dispatch(apiRequestSuccess({ make: results, formIsDirty: true }));
  } catch (e) {
    if (!axios.isCancel(e)) {
      dispatch(
        apiRequestFailure(new Error(`Makes request error: ${e.message}`)),
      );
    }
  }
};

// Fetch the model options
const fetchModelOptions = async (make, dispatch, source) => {
  try {
    const {
      data: { results },
    } = await axios({
      url: `${DOMAIN}/cars?make=${encodeURI(make)}`,
      cancelToken: source.token,
    });

    dispatch(
      apiRequestSuccess({
        model: results.optionsModels,
        bodyStyles: results.bodyStylesMap,
      }),
    );
  } catch (e) {
    if (!axios.isCancel(e)) {
      dispatch(
        apiRequestFailure(new Error(`Models request error: ${e.message}`)),
      );
    }
  }
};

// Fetch the version options
const fetchVersionOptions = async (make, model, state, dispatch, source) => {
  // The bodystyle value is needed to make the final call for 'version/derivatives'.
  // So here we are using a map (created during the 'models' API call) to find
  // the matching bodystyle.
  const { bodyStyle, modelId, trim } = state.bodyStyles[model];

  try {
    const {
      data: { results },
    } = await axios({
      url: `${DOMAIN}/cars`,
      params: {
        make: encodeURI(make),
        model: modelId,
        bodystyle: bodyStyle,
        ...(trim && { trim }),
      },
      cancelToken: source.token,
    });

    dispatch(
      apiRequestSuccess({
        ...versionOptions(results),
        results,
      }),
    );
  } catch (e) {
    if (!axios.isCancel(e)) {
      dispatch(
        apiRequestFailure(new Error(`Versions request error: ${e.message}`)),
      );
    }
  }
};

// On subsequent select box changes, we fire
// off functions depending on which select was chosen.
const handleChangeFuncs = ({
  getValues,
  state,
  dispatch,
  modelSource,
  versionSource,
}) => {
  return {
    make: () => {
      fetchModelOptions(getValues('make').value, dispatch, modelSource);
    },
    model: () => {
      fetchVersionOptions(
        getValues('make').value,
        getValues('model').value,
        state,
        dispatch,
        versionSource,
      );
    },
    version: () => {
      const { label } = getValues('version');
      const version = state.results.find((version) => label === version.name);
      dispatch(selectedVersion(version));
    },
  };
};

const useAPICarData = (updatedSelect, getValues, state, dispatch) => {
  useEffect(() => {
    const makeSource = axios.CancelToken.source();
    const modelSource = axios.CancelToken.source();
    const versionSource = axios.CancelToken.source();

    // On load, get the 'make' select options
    if (!state.make.length) {
      fetchMakeOptions(dispatch, makeSource);
    }

    // Then on subsequent updates of the select boxes, we fetch new options
    // for make/model/verions (only when a new value is selected)
    if (
      updatedSelect &&
      getValues([updatedSelect.name])?.value !== updatedSelect.newValue
    ) {
      if (updatedSelect.name === 'year') return;

      handleChangeFuncs({
        getValues,
        state,
        dispatch,
        modelSource,
        versionSource,
      })[updatedSelect.name]();
    }

    return () => {
      makeSource.cancel();
      modelSource.cancel();
      versionSource.cancel();
    };
  }, [updatedSelect]);
};

export default useAPICarData;
